import { FormScope } from '@/constants';
import {
  DocumentTypeChoices,
  IODCatchmentAreaForm,
  IODCompanyForm,
  IODEquipmentForm,
  IODSkillsForm,
  IODUserForm,
  IOnboardingForms,
  KbisDocument,
  ODDocumentsForm,
  OptionalDocumentProps,
  RcpDocument,
  UserDocument,
} from '@/model';
import store from '@/store';
import {
  AgencyDocumentTypeChoices,
  CompanyDocumentTypeChoices,
  IAgencyDocument,
  ICompanyDocument,
  ITraderAddress,
  IUser,
} from '@homeserve/od-typings/api';
import { Nullable, Optional } from '@homeserve/od-typings/utils';
import { IAPIError } from '@homeserve/od-typings/web';
import { agencyAdminHelper, companyHelper, IRegion } from '@homeserve/vue-api-plugin';
import { Auth0Error } from 'auth0-js';

export function isAuthError(error: any): error is Auth0Error {
  return !!(error && (error as Auth0Error).code);
}
export function isApiError(error: any): error is IAPIError {
  return !!(error && (error as IAPIError).response);
}

export function isKbisDocument(document: UserDocument): document is KbisDocument {
  return !document.hasOwnProperty('validityEndDate') && document.hasOwnProperty('validityStartDate');
}

export function isRcpDocument(document: UserDocument): document is RcpDocument {
  return document.hasOwnProperty('validityEndDate') && document.hasOwnProperty('validityStartDate');
}

export function buildAddress(data: any): Nullable<ITraderAddress> {
  const { country, postalBox, postCode, street, streetNo, town } = data;

  if (country && postCode && street && town) {
    const address: ITraderAddress = {
      country,
      postCode: '',
      street: '',
      town: '',
    };

    if (postalBox) {
      address.postalBox = postalBox;
    }

    if (postCode) {
      address.postCode = postCode;
    }

    if (street) {
      address.street = street;
    }

    if (streetNo) {
      address.streetNo = streetNo;
    }

    if (town) {
      address.town = town;
    }

    return address;
  }

  return null;
}

export function buildCompanyFormModel(user: IUser, model: IODCompanyForm): IODCompanyForm {
  const { company } = user;

  if (!company) {
    return model;
  }

  const companyFormModel = {
    ...model,
  };

  const {
    address,
    bankAccount,
    creationDate,
    isVatApplicable,
    legalForm,
    managerContact: { birthCity, birthCountry, birthDate, firstName, lastName, personalAddress },
    name,
    rcsCity,
    registrationNumber,
  } = company;

  if (address) {
    companyFormModel.address = buildAddress(address);
  }

  if (birthCity) {
    companyFormModel.birthCity = birthCity;
  }

  if (birthCountry) {
    companyFormModel.birthCountry = birthCountry;
  }

  if (birthDate) {
    companyFormModel.birthDate = (birthDate as unknown as string).substr(0, 10);
  }

  if (name) {
    companyFormModel.companyName = name;
  }

  if (creationDate) {
    companyFormModel.creationDate = (creationDate as unknown as string).substr(0, 10);
  }

  if (bankAccount) {
    const { iban, holderName } = bankAccount;

    if (holderName) {
      companyFormModel.holderName = holderName;
    }

    if (iban) {
      companyFormModel.iban = iban;
    }
  }

  companyFormModel.isVatApplicable = isVatApplicable;

  if (legalForm) {
    companyFormModel.legalForm = legalForm;
  }

  if (personalAddress) {
    companyFormModel.personalAddress = buildAddress(personalAddress);
  } else if (companyFormModel.address) {
    companyFormModel.personalAddress = { ...companyFormModel.address };
  }

  if (rcsCity) {
    companyFormModel.rcsCity = rcsCity;
  }

  if (!companyFormModel.holderName) {
    companyFormModel.holderName = `${firstName} ${lastName}`;
  }

  return companyFormModel;
}

export function buildCatchmentAreaFormModel(
  user: IUser,
  model: IODCatchmentAreaForm,
  agencyId?: number,
): IODCatchmentAreaForm {
  const { company } = user;
  let catchmentArea: number = 0;
  let workingPostCodes: string[] = [];

  if (!company || !Array.isArray(company.agencies) || !company.agencies.length) {
    return model;
  }

  if (agencyId) {
    const selectedAgency = company.agencies.find(agency => agency.id === agencyId);

    if (selectedAgency) {
      ({ catchmentArea, workingPostCodes } = selectedAgency);
    }
  } else {
    ({ catchmentArea, workingPostCodes } = company.agencies[0]);
  }
  const departments = [...new Set(workingPostCodes.map(postCode => postCode.substring(0, 2)))].sort();

  return {
    catchmentArea,
    departments,
  };
}

export function buildDocumentModel(
  type: DocumentTypeChoices,
  file: Nullable<File> = null,
  rcpNumMandatory = true,
  optionalProps?: OptionalDocumentProps,
) {
  let documentModel: UserDocument = {
    file,
  };

  if (
    [CompanyDocumentTypeChoices.KBIS, AgencyDocumentTypeChoices.RESPONSABILITE_CIVILE_PROFESSIONNELLE].indexOf(type) !==
    -1
  ) {
    documentModel = {
      ...documentModel,
      validityStartDate:
        optionalProps && optionalProps.validityStartDate ? optionalProps.validityStartDate.substr(0, 10) : null,
    };
  }

  if (type === AgencyDocumentTypeChoices.RESPONSABILITE_CIVILE_PROFESSIONNELLE) {
    documentModel = {
      ...documentModel,
      validityEndDate:
        optionalProps && optionalProps.validityEndDate ? optionalProps.validityEndDate.substr(0, 10) : null,
    };

    if (rcpNumMandatory) {
      documentModel = {
        ...documentModel,
        rcpNum: optionalProps ? optionalProps.rcpNum : null,
      };
    }
  }

  return documentModel;
}

export async function buildDocumentsFormModel(
  user: IUser,
  model: ODDocumentsForm,
  downloadFiles = true,
): Promise<ODDocumentsForm> {
  const { company } = user;
  let firstAgency: Optional<any>;
  let documents: Array<ICompanyDocument | IAgencyDocument> = [];

  if (!company || !Array.isArray(company.documents)) {
    return model;
  }

  documents = documents.concat(company.documents);

  if (Array.isArray(company.agencies)) {
    firstAgency = company.agencies[0];

    if (firstAgency && Array.isArray(firstAgency.documents)) {
      documents = documents.concat(firstAgency.documents);
    }
  }

  const promises = documents
    .filter(
      ({ type }) =>
        [
          AgencyDocumentTypeChoices.RESPONSABILITE_CIVILE_PROFESSIONNELLE,
          CompanyDocumentTypeChoices.IBAN,
          CompanyDocumentTypeChoices.KBIS,
        ].indexOf(type) !== -1,
    )
    .map(async ({ id: documentId, type, document }) => {
      const { name: fileName } = document;
      const { error, result } = downloadFiles
        ? Object.values(CompanyDocumentTypeChoices).indexOf(type) !== -1
          ? await companyHelper.downloadDocument(store, {
              companyId: company.id,
              documentId,
              fileName,
            })
          : await agencyAdminHelper.downloadDocument(store, {
              agencyId: firstAgency.id,
              companyId: company.id,
              documentId,
              fileName,
            })
        : { result: model[type].file, error: null };
      const file = !error ? result || null : null;
      const validityEndDate: Optional<string> = document.validityEndDate as unknown as string;
      const validityStartDate: Optional<string> = document.validityStartDate as unknown as string;
      const documentModel = buildDocumentModel(type, file, true, {
        validityEndDate,
        validityStartDate,
        rcpNum:
          firstAgency && type === AgencyDocumentTypeChoices.RESPONSABILITE_CIVILE_PROFESSIONNELLE
            ? firstAgency.rcpNum
            : undefined,
      });

      return {
        [type]: documentModel,
      };
    });

  const responses = await Promise.all(promises);

  return responses.reduce<ODDocumentsForm>((accumulator, formModel) => {
    accumulator = {
      ...accumulator,
      ...formModel,
    };

    return accumulator;
  }, model);
}

export function buildEquipmentFormModel(user: IUser, model: IODEquipmentForm, agencyId?: number): IODEquipmentForm {
  const { company } = user;
  let equipment: string[] = [];

  if (!company || !Array.isArray(company.agencies) || !company.agencies[0]) {
    return model;
  }

  if (agencyId) {
    const selectedAgency = company.agencies.find(agency => agency.id === agencyId);

    if (selectedAgency) {
      ({ equipment } = selectedAgency);
    }
  } else {
    ({ equipment } = company.agencies[0]);
  }

  return {
    equipment,
  };
}

export function buildSkillsFormModel(user: IUser, model: IODSkillsForm, agencyId?: number): IODSkillsForm {
  const { company } = user;
  let domains: string[] = [];
  let products: string[] = [];

  if (!company || !Array.isArray(company.agencies) || !company.agencies[0]) {
    return model;
  }

  if (agencyId) {
    const selectedAgency = company.agencies.find(agency => agency.id === agencyId);

    if (selectedAgency) {
      ({ domains, products } = selectedAgency);
    }
  } else {
    ({ domains, products } = company.agencies[0]);
  }

  return {
    domains,
    products,
  };
}

export function buildUserFormModel(user: IUser, model: IODUserForm): IODUserForm {
  const { company, email, postCode } = user;
  let registrationNumber: string | undefined | null;
  if (user.company) {
    registrationNumber = user.company.registrationNumber;
  }

  if (!company) {
    return model;
  }

  const userFormModel = {
    ...model,
    email,
    postCode,
    password: '',
  };

  const {
    managerContact: { firstName, lastName, phoneMobile },
  } = company;

  if (firstName) {
    userFormModel.firstName = firstName;
  }

  if (lastName) {
    userFormModel.lastName = lastName;
  }

  if (phoneMobile) {
    userFormModel.phone = phoneMobile;
  }

  if (registrationNumber) {
    userFormModel.registrationNumber = registrationNumber;
  }

  return userFormModel;
}

export async function buildOnboardingModel(
  user: IUser,
  model: IOnboardingForms,
  downloadFiles = true,
): Promise<IOnboardingForms> {
  const finalModel = {
    ...model,
    [FormScope.CATCHMENT_AREA]: buildCatchmentAreaFormModel(user, model[FormScope.CATCHMENT_AREA]),
    [FormScope.COMPANY]: buildCompanyFormModel(user, model[FormScope.COMPANY]),
    [FormScope.DOCUMENTS]: await buildDocumentsFormModel(user, model[FormScope.DOCUMENTS], downloadFiles),
    [FormScope.EQUIPMENT]: buildEquipmentFormModel(user, model[FormScope.EQUIPMENT]),
    [FormScope.SKILLS]: buildSkillsFormModel(user, model[FormScope.SKILLS]),
    [FormScope.USER]: buildUserFormModel(user, model[FormScope.USER]),
  };

  return finalModel;
}

export function departmentsByRegion(regions: IRegion[], catchmentData: IODCatchmentAreaForm) {
  const sortBylabel = (a: IRegion, b: IRegion) => {
    if (a.label < b.label) {
      return -1;
    }

    if (a.label > b.label) {
      return 1;
    }

    return 0;
  };

  return regions
    .reduce<IRegion[]>((accumulator, region) => {
      const departments = region.departments.filter(
        department => catchmentData.departments.indexOf(department.departmentCode) !== -1,
      );

      if (!departments.length) {
        return accumulator;
      }

      accumulator.push({
        ...region,
        departments,
      });

      return accumulator;
    }, [])
    .sort(sortBylabel);
}

export function formatAddress(address: ITraderAddress) {
  const { postCode, street, streetNo, town } = address;

  return [[streetNo, street].join(' '), [postCode, town].join(' ')].join('-');
}

export function generateWildCardFromDepartments(departments: string[]) {
  return departments.map(department => `${department}*`);
}
